import React, { useCallback, useEffect, useRef, useState } from "react";
import "./UserView.css";
import "../tables/Button.css";
import "../SafetyObservation/safetyObservation.css";
import { useNavigate, useParams } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import axios from "axios";
import { IoMdArrowBack } from "react-icons/io";
import { IoCloseOutline } from "react-icons/io5";
import { FiDownload, FiEye, FiUpload } from "react-icons/fi";
import API_URL from "../../src/Config";
import {
  OBSERVATION_STATUS_OPTIONS,
  downloadSafetyFile,
  formatDateDisplay,
  getObservationSeverityLabel,
  getObservationStatusLabel,
  getObservationTypeLabel,
  getStatusColor,
  openSafetyFile,
  parseApiError,
} from "../SafetyObservation/observationUtils";

function SafetyObservationView({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const { observationId } = useParams();
  const fileInputRefs = useRef({});
  const [observation, setObservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [actionStatuses, setActionStatuses] = useState({});
  const [actionFiles, setActionFiles] = useState({});
  const [updatingActionId, setUpdatingActionId] = useState("");
  const [actionToDelete, setActionToDelete] = useState(null);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [isDeletingAction, setIsDeletingAction] = useState(false);
  const [isDeletingAttachment, setIsDeletingAttachment] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const fetchObservation = useCallback(async () => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");

      const response = await axios.get(
        `${API_URL}/api/safety/observations/${observationId}`,
        {
          headers: {
            Authorization: authKey,
          },
        },
      );

      const observationData = response.data || {};
      const nextActionStatuses = {};

      (observationData.actions || []).forEach((action) => {
        nextActionStatuses[action.id] = action.status || "open";
      });

      setObservation(observationData);
      setActionStatuses(nextActionStatuses);
      setActionFiles({});
    } catch (error) {
      showSnackbar(
        parseApiError(error, "Failed to fetch observation"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [observationId]);

  useEffect(() => {
    fetchObservation();
  }, [fetchObservation]);

  const getSelectedActionStatus = (action) =>
    actionStatuses[action.id] || action.status || "open";

  const handleActionStatusChange = (actionId, status) => {
    setActionStatuses((prevState) => ({
      ...prevState,
      [actionId]: status,
    }));

    if (status !== "closed") {
      setActionFiles((prevState) => {
        const nextState = { ...prevState };
        delete nextState[actionId];
        return nextState;
      });
    }
  };

  const handleActionFileChange = (actionId, files) => {
    const selectedFiles = Array.from(files || []);
    if (selectedFiles.length === 0) return;

    setActionFiles((prevState) => ({
      ...prevState,
      [actionId]: [...(prevState[actionId] || []), ...selectedFiles],
    }));

    if (fileInputRefs.current[actionId]) {
      fileInputRefs.current[actionId].value = "";
    }
  };

  const handleRemovePendingFile = (actionId, fileIndex) => {
    setActionFiles((prevState) => {
      const nextFiles = [...(prevState[actionId] || [])];
      nextFiles.splice(fileIndex, 1);

      const nextState = { ...prevState };
      if (nextFiles.length > 0) {
        nextState[actionId] = nextFiles;
      } else {
        delete nextState[actionId];
      }

      return nextState;
    });
  };

  const handleDownload = async (attachment) => {
    try {
      await downloadSafetyFile(attachment.id, attachment.filename);
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to download file"), "error");
    }
  };

  const handleViewFile = async (attachment) => {
    try {
      await openSafetyFile(attachment.id);
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to open file"), "error");
    }
  };

  const handleUpdateAction = async (action) => {
    try {
      setUpdatingActionId(action.id);
      const authKey = localStorage.getItem("authKey");
      const formData = new FormData();
      const selectedStatus = getSelectedActionStatus(action);
      const selectedFiles = actionFiles[action.id] || [];

      formData.append("status", selectedStatus);
      selectedFiles.forEach((file) => {
        formData.append("files", file);
      });

      const response = await axios.put(
        `${API_URL}/api/safety/observations/${observationId}/actions/${action.id}`,
        formData,
        {
          headers: {
            Authorization: authKey,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      showSnackbar(response.data?.message || "Action updated successfully");
      await fetchObservation();
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to update action"), "error");
    } finally {
      setUpdatingActionId("");
    }
  };

  const handleDeleteAction = async () => {
    if (!actionToDelete) return;

    try {
      setIsDeletingAction(true);
      const authKey = localStorage.getItem("authKey");

      const response = await axios.delete(
        `${API_URL}/api/safety/observations/${observationId}/actions/${actionToDelete.id}`,
        {
          headers: {
            Authorization: authKey,
          },
        },
      );

      showSnackbar(response.data?.message || "Action deleted successfully");
      setActionToDelete(null);
      await fetchObservation();
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to delete action"), "error");
    } finally {
      setIsDeletingAction(false);
    }
  };

  const handleDeleteAttachment = async () => {
    if (!attachmentToDelete) return;

    try {
      setIsDeletingAttachment(true);
      const authKey = localStorage.getItem("authKey");

      const response = await axios.delete(
        `${API_URL}/api/safety/observations/actions/attachments/${attachmentToDelete.id}`,
        {
          headers: {
            Authorization: authKey,
          },
        },
      );

      showSnackbar(response.data?.message || "Attachment deleted successfully");
      setAttachmentToDelete(null);
      await fetchObservation();
    } catch (error) {
      showSnackbar(
        parseApiError(error, "Failed to delete attachment"),
        "error",
      );
    } finally {
      setIsDeletingAttachment(false);
    }
  };

  const handleDeleteObservation = async () => {
    try {
      setIsDeleting(true);
      const authKey = localStorage.getItem("authKey");

      const response = await axios.delete(
        `${API_URL}/api/safety/observations/${observationId}`,
        {
          headers: {
            Authorization: authKey,
          },
        },
      );

      showSnackbar(
        response.data?.message || "Observation deleted successfully",
      );
      setShowDeleteConfirm(false);
      window.setTimeout(() => {
        navigate("/safety-observations");
      }, 700);
    } catch (error) {
      showSnackbar(
        parseApiError(error, "Failed to delete observation"),
        "error",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content observation-view-page">
          <div className="data-list-header">
            <div className="data-list-header-content">
              <div className="data-list-heading-content-left-side">
                <Tooltip title="Back" arrow>
                  <IoMdArrowBack
                    className="back-company"
                    onClick={() => navigate("/safety-observations")}
                  />
                </Tooltip>
                Observation View
              </div>
              {/* <div className="observation-view-actions">
                <button
                  className="button-bg"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete
                </button>
                <button
                  className="button-bg"
                  onClick={() =>
                    navigate(`/edit-safety-observation/${observationId}`)
                  }
                >
                  Edit
                </button>
              </div> */}
            </div>
          </div>

          <div className="header-separator"></div>

          {loading ? (
            <div className="loader-container">
              <div className="loader"></div>
              <div>Loading...</div>
            </div>
          ) : observation ? (
            <>
              <div className="observation-section-card">
                <div className="observation-section-title">
                  Observation Details
                </div>
                <div className="observation-section-body">
                  <div className="observation-detail-grid">
                    <div className="observation-detail-item">
                      <span className="observation-detail-label">Title</span>
                      <span>{observation.title || "NA"}</span>
                    </div>
                    <div className="observation-detail-item">
                      <span className="observation-detail-label">Type</span>
                      <span>{getObservationTypeLabel(observation.type)}</span>
                    </div>
                    <div className="observation-detail-item">
                      <span className="observation-detail-label">Severity</span>
                      <span>
                        {getObservationSeverityLabel(observation.severity)}
                      </span>
                    </div>
                    {/* <div className="observation-detail-item">
                      <span className="observation-detail-label">Status</span>
                      <span
                        className="observation-status-pill"
                        style={{
                          backgroundColor: getStatusColor(observation.status),
                          display: "inline-block",
                        }}
                      >
                        {getObservationStatusLabel(observation.status)}
                      </span>
                    </div> */}
                    <div className="observation-detail-item">
                      <span className="observation-detail-label">
                        Department
                      </span>
                      <span>{observation.department_name || "NA"}</span>
                    </div>
                    <div className="observation-detail-item">
                      <span className="observation-detail-label">Location</span>
                      <span>{observation.location || "NA"}</span>
                    </div>
                    <div className="observation-detail-item">
                      <span className="observation-detail-label">
                        Observed Date
                      </span>
                      <span>
                        {formatDateDisplay(observation.observed_date)}
                      </span>
                    </div>
                    <div className="observation-detail-item">
                      <span className="observation-detail-label">
                        Observer Name
                      </span>
                      <span>{observation.observer_name || "NA"}</span>
                    </div>
                    <div className="observation-detail-item form-group-full-width">
                      <span className="observation-detail-label">
                        Description
                      </span>
                      <span>{observation.description || "NA"}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="observation-section-card">
                <div className="observation-section-title">
                  Observation Documents
                </div>
                <div className="observation-section-body">
                  {Array.isArray(observation.attachments) &&
                  observation.attachments.length > 0 ? (
                    <div className="observation-attachments">
                      {observation.attachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          className="observation-attachment-row"
                        >
                          <div className="observation-attachment-meta">
                            <div className="observation-attachment-name">
                              {attachment.filename}
                            </div>
                            <div className="observation-attachment-subtext">
                              {attachment.file_type || "NA"} |{" "}
                              {attachment.file_size || 0} KB
                            </div>
                          </div>
                          <div className="observation-attachment-actions">
                            <Tooltip title="View" arrow>
                              <button
                                className="observation-icon-button"
                                type="button"
                                onClick={() => handleViewFile(attachment)}
                              >
                                <FiEye />
                              </button>
                            </Tooltip>
                            <Tooltip title="Download" arrow>
                              <button
                                className="observation-icon-button"
                                type="button"
                                onClick={() => handleDownload(attachment)}
                              >
                                <FiDownload />
                              </button>
                            </Tooltip>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="observation-empty-state">
                      No documents uploaded for this observation.
                    </div>
                  )}
                </div>
              </div>

              <div className="observation-section-card">
                <div className="observation-section-title">
                  Assigned Actions
                </div>
                <div className="observation-section-body">
                  {Array.isArray(observation.actions) &&
                  observation.actions.length > 0 ? (
                    <div className="observation-actions-list">
                      {observation.actions.map((action) => {
                        const selectedStatus = getSelectedActionStatus(action);
                        const pendingFiles = actionFiles[action.id] || [];
                        const isUpdatingCurrentAction =
                          updatingActionId === action.id;
                        const isActionClosed = action.status === "closed";
                        const canUpdateAction =
                          selectedStatus !== (action.status || "open") ||
                          pendingFiles.length > 0;

                        return (
                          <div
                            key={action.id}
                            className="observation-action-card"
                          >
                            <div className="observation-action-header">
                              <div className="observation-action-meta">
                                <span>
                                  <strong>Assignee:</strong>{" "}
                                  {action.assigned_to_name || "NA"}
                                </span>
                                <span>
                                  <strong>Due:</strong>{" "}
                                  {formatDateDisplay(action.due_date)}
                                </span>
                                <span>
                                  <strong>Created By:</strong>{" "}
                                  {action.created_by_name || "NA"}
                                </span>
                                <span>
                                  <strong>Closed At:</strong>{" "}
                                  {formatDateDisplay(action.closed_at)}
                                </span>
                              </div>
                              <span
                                className="observation-status-pill"
                                style={{
                                  backgroundColor:
                                    getStatusColor(selectedStatus),
                                }}
                              >
                                {getObservationStatusLabel(selectedStatus)}
                              </span>
                            </div>

                            <div className="observation-action-description">
                              {action.description || "NA"}
                            </div>

                            <div className="observation-action-controls">
                              <div className="observation-action-status-field">
                                <label htmlFor={`action-status-${action.id}`}>
                                  Update Status
                                </label>
                                <div className="observation-action-status-row">
                                  {isActionClosed ? (
                                    <div
                                      className="observation-status-pill"
                                      style={{
                                        backgroundColor: getStatusColor(
                                          action.status,
                                        ),
                                      }}
                                    >
                                      {getObservationStatusLabel(action.status)}
                                    </div>
                                  ) : (
                                    <select
                                      id={`action-status-${action.id}`}
                                      className="observation-action-select"
                                      value={selectedStatus}
                                      onChange={(event) =>
                                        handleActionStatusChange(
                                          action.id,
                                          event.target.value,
                                        )
                                      }
                                    >
                                      {OBSERVATION_STATUS_OPTIONS.map(
                                        (option) => (
                                          <option
                                            key={option.value}
                                            value={option.value}
                                          >
                                            {option.label}
                                          </option>
                                        ),
                                      )}
                                    </select>
                                  )}

                                  {selectedStatus === "closed" && (
                                    <div className="observation-action-upload">
                                      <Tooltip title="Upload Documents" arrow>
                                        <button
                                          className="observation-icon-button"
                                          type="button"
                                          onClick={() =>
                                            fileInputRefs.current[
                                              action.id
                                            ]?.click()
                                          }
                                        >
                                          <FiUpload />
                                        </button>
                                      </Tooltip>
                                      <input
                                        ref={(element) => {
                                          fileInputRefs.current[action.id] =
                                            element;
                                        }}
                                        className="observation-hidden-file-input"
                                        type="file"
                                        multiple
                                        onChange={(event) =>
                                          handleActionFileChange(
                                            action.id,
                                            event.target.files,
                                          )
                                        }
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {pendingFiles.length > 0 && (
                              <div className="observation-selected-files">
                                {pendingFiles.map((file, index) => (
                                  <div
                                    key={`${file.name}-${file.size}-${index}`}
                                    className="observation-selected-file"
                                  >
                                    <span>{file.name}</span>
                                    <button
                                      className="observation-icon-button observation-icon-button-danger"
                                      type="button"
                                      onClick={() =>
                                        handleRemovePendingFile(
                                          action.id,
                                          index,
                                        )
                                      }
                                    >
                                      <IoCloseOutline />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {Array.isArray(action.attachments) &&
                            action.attachments.length > 0 ? (
                              <div className="observation-attachments">
                                {action.attachments.map((attachment) => (
                                  <div
                                    key={attachment.id}
                                    className="observation-attachment-row"
                                  >
                                    <div className="observation-attachment-meta">
                                      <div className="observation-attachment-name">
                                        {attachment.filename}
                                      </div>
                                      <div className="observation-attachment-subtext">
                                        {attachment.file_type || "NA"} |{" "}
                                        {attachment.file_size || 0} KB
                                      </div>
                                    </div>
                                    <div className="observation-attachment-actions">
                                      <Tooltip title="View" arrow>
                                        <button
                                          className="observation-icon-button"
                                          type="button"
                                          onClick={() =>
                                            handleViewFile(attachment)
                                          }
                                        >
                                          <FiEye />
                                        </button>
                                      </Tooltip>
                                      <Tooltip title="Download" arrow>
                                        <button
                                          className="observation-icon-button"
                                          type="button"
                                          onClick={() =>
                                            handleDownload(attachment)
                                          }
                                        >
                                          <FiDownload />
                                        </button>
                                      </Tooltip>
                                      {/* <Tooltip title="Delete" arrow>
                                        <button
                                          className="observation-icon-button observation-icon-button-danger"
                                          type="button"
                                          onClick={() =>
                                            setAttachmentToDelete(attachment)
                                          }
                                        >
                                          <FiTrash2 />
                                        </button>
                                      </Tooltip> */}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="observation-empty-state">
                                No attachments found for this action.
                              </div>
                            )}

                            <div className="observation-action-footer">
                              <button
                                className="button-bg"
                                type="button"
                                onClick={() => handleUpdateAction(action)}
                                disabled={
                                  isUpdatingCurrentAction || !canUpdateAction
                                }
                              >
                                {isUpdatingCurrentAction
                                  ? "Updating..."
                                  : "Update Action"}
                              </button>
                              {/* <button
                                className="button-bg"
                                type="button"
                                onClick={() => setActionToDelete(action)}
                                disabled={isUpdatingCurrentAction}
                              >
                                Delete Action
                              </button> */}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="observation-empty-state">
                      No actions assigned yet.
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="no-users-container">
              <div className="no-users-message">Observation not found</div>
            </div>
          )}
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="confirm-delete-popup">
          <div className="confirm-delete-popup-content">
            <div className="confirm-model">
              <div className="confirm-model-content">
                <div className="confirm-model-content-body">
                  <p>
                    Are you sure you want to delete observation{" "}
                    <strong>{observation?.title || "this item"}</strong>?
                  </p>
                </div>
                <div className="confirm-model-content-footer">
                  <button
                    className="confirm-delete-button"
                    onClick={handleDeleteObservation}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Deleting..." : "Yes"}
                  </button>
                  <button
                    className="confirm-cancel-button"
                    onClick={() => !isDeleting && setShowDeleteConfirm(false)}
                    disabled={isDeleting}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {actionToDelete && (
        <div className="confirm-delete-popup">
          <div className="confirm-delete-popup-content">
            <div className="confirm-model">
              <div className="confirm-model-content">
                <div className="confirm-model-content-body">
                  <p>
                    Are you sure you want to delete this action?
                    <strong>
                      {" "}
                      {actionToDelete.description || "Selected action"}
                    </strong>
                  </p>
                </div>
                <div className="confirm-model-content-footer">
                  <button
                    className="confirm-delete-button"
                    onClick={handleDeleteAction}
                    disabled={isDeletingAction}
                  >
                    {isDeletingAction ? "Deleting..." : "Yes"}
                  </button>
                  <button
                    className="confirm-cancel-button"
                    onClick={() => !isDeletingAction && setActionToDelete(null)}
                    disabled={isDeletingAction}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {attachmentToDelete && (
        <div className="confirm-delete-popup">
          <div className="confirm-delete-popup-content">
            <div className="confirm-model">
              <div className="confirm-model-content">
                <div className="confirm-model-content-body">
                  <p>
                    Are you sure you want to delete attachment{" "}
                    <strong>
                      {attachmentToDelete.filename || "this file"}
                    </strong>
                    ?
                  </p>
                </div>
                <div className="confirm-model-content-footer">
                  <button
                    className="confirm-delete-button"
                    onClick={handleDeleteAttachment}
                    disabled={isDeletingAttachment}
                  >
                    {isDeletingAttachment ? "Deleting..." : "Yes"}
                  </button>
                  <button
                    className="confirm-cancel-button"
                    onClick={() =>
                      !isDeletingAttachment && setAttachmentToDelete(null)
                    }
                    disabled={isDeletingAttachment}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default SafetyObservationView;
